/* eslint-disable import/order */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, equals, isNil } from 'ramda';
import { Formik, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import { IoIosArrowBack } from 'react-icons/io';
import { Fade } from 'react-awesome-reveal';

import { useGetParams } from '../utils/useGetParams';
import { ROUTES } from 'utils/constants';
import { handleTrackEvent } from '../utils/handleTrackEvent';
import { useEvieOnboardingContext } from '../EvieOnboardingProvider';
import evieLogo from 'assets/images/evie-logo-qs.png';

import FormControlSurveys from 'components/Surveys/FormControlSurveys';
import Button from 'components/SolidButton';

const Survey = ({ survey, completeSurvey }) => {
  const { content: groups = [] } = survey;
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { step, group, question, push, goBack } = useGetParams();
  const { params } = useEvieOnboardingContext();

  const [queryGroups, setQueryGroups] = useState(groups);
  const [currentStep, setCurrentStep] = useState(0);

  const totalSteps = groups.reduce((acc, curr) => acc + curr.questions.length, 0);
  const percentageCompleted = (currentStep * 100) / totalSteps;
  const currentQuestion = groups
    .find((_, gIndex) => gIndex + 1 === group)
    .questions.find((_, qIndex) => qIndex + 1 === question);
  const isInfoScreen = currentQuestion.type === 'text-image';
  const isLastQuestion = groups[groups.length - 1].questions.length === question;
  const isSurveyCompleted = group === queryGroups.length && isLastQuestion && !equals(groups, queryGroups);

  useEffect(() => {
    if (isSurveyCompleted) {
      completeSurvey(queryGroups);
    }
  }, [group, queryGroups]);

  const initialFormState = (id) => {
    const findGroupToValidate = queryGroups.find(({ id: validateId }) => validateId === id);
    const initial = {};
    findGroupToValidate.questions.forEach((currQuestion) => {
      const { id: questionId = '', type = 'text', label = '', value = '', multi } = { ...currQuestion };

      switch (type) {
        case 'verification':
          initial[questionId] = false || value;
          break;
        case 'block':
          initial[questionId] = label;
          break;
        case 'multiple-selection':
          initial[questionId] = multi ? value.split(', ').filter(Boolean) || '' : '' || value;
          break;
        case 'pointed-multiple-selection':
          initial[questionId] = '' || value.label;
          break;
        default:
          initial[questionId] = '' || value;
      }
    });
    if (findGroupToValidate.files) {
      findGroupToValidate.files.forEach(({ file, questionId }) => {
        initial[questionId] = file;
      });
    }
    return initial;
  };

  const getValue = ({ id: valueId, options, values }) => {
    if (Array.isArray(values[valueId])) {
      return values[valueId].join(', ');
    }
    if (['object'].includes(typeof options[0])) {
      let foundOption = options.find(({ label }) => label === values[valueId]);
      
      if (!foundOption) {
        foundOption = options.find(({ value }) => value === values[valueId]);
      }
      
      if (!foundOption) {
        foundOption = options.find(({ label }) => 
          label && values[valueId] && label.toLowerCase().includes(values[valueId].toLowerCase())
        );
      }
      
      const result = foundOption || values[valueId] || '';
      return result;
    }
    return isNil(values[valueId]) ? '' : values[valueId];
  };

  const handleAddGroup = (values, groupId) => {
    const currentGroup = groups.find(({ id: queryGroupId }) => queryGroupId === groupId);
    setQueryGroups((prevQueryGroups) =>
      prevQueryGroups.map((currGroup) => {
        if (currGroup.id === groupId) {
          return {
            ...currGroup,
            questions: currGroup.questions
              .filter(({ type }) => type !== 'file')
              .map((currQuestion) => ({
                ...currQuestion,
                value: getValue({ id: currQuestion.id, options: currQuestion.options || [], values }),
              })),
            files: currentGroup.questions
              .filter(({ type, id }) => type === 'file' && !isEmpty(values[id]) && values[id] !== undefined)
              .map(({ id: fileId, label }) => ({
                groupId: currGroup.id,
                label,
                file: values[fileId],
              })),
          };
        }
        return currGroup;
      }),
    );
    setCurrentStep((prev) => prev + 1);
    if (!isSurveyCompleted) {
      handleTrackEvent('Step clicked', { number: step + 1 });
      push(
        `${ROUTES.EVIE_ONBOARDING}?${params}&step=${step + 1}&group=${group + 1}&question=${question - question + 1}`,
      );
    }

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleBack = ({ group: currGroup, question: currStep, prevGroup }) => {
    const goPrevious = currStep === 1;
    const newGroup = goPrevious ? currGroup - 1 : currGroup;
    const newQuestion = goPrevious ? prevGroup.questions.length : currStep - 1;

    setCurrentStep((prev) => prev - 1);
    goBack(`${ROUTES.EVIE_ONBOARDING}?step=${step - 1}&group=${newGroup}&question=${newQuestion}`);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const questionhandler = ({ goNextGroup, submitForm }) => {
    if (goNextGroup) {
      submitForm();
    } else {
      setCurrentStep((prev) => prev + 1);
      handleTrackEvent('Step clicked', { number: step + 1 });
      push(`${ROUTES.EVIE_ONBOARDING}?${params}&step=${step + 1}&group=${group}&question=${question + 1}`);
    }
  };

  return (
    <section className={`sections ${isInfoScreen ? 'info-screen-survey' : ''}`}>
      {!isInfoScreen && (
        <>
          <div className="sections__logo">
            <img src={evieLogo} alt="evie logo" />
          </div>
          <div className='sections__navigation'>
            <div>
            {(group > 1 || question > 1) && (
              <IoIosArrowBack
                className="sections__goback"
                onClick={() => handleBack({ group, question, prevGroup: groups[group - 2] })}
              />
            )}  
            </div> 
            <div className="sections__progress">
              <div className="sections__completed" style={{ width: `${percentageCompleted}%` }} />
            </div>
          </div>
        </>
      )}
      {groups.map((groupInfo, groupIndex) => {
        const { id = '', questions = [] } = { ...groupInfo };
        const initialValues = initialFormState(id);

        if (groupIndex + 1 !== group) return null;

        return (
          <div key={id} className="sections__form">
            <Formik
              validationSchema={null}
              initialValues={initialValues}
              onSubmit={(values) => handleAddGroup(values, id)}
            >
              {(formData) => (
                <Form>
                  {questions.map((currQuestion, index) => {
                    const {
                      type = 'text',
                      label = '',
                      title = '',
                      content = '',
                      gif = '',
                      id: questionId = '',
                      required = false,
                      includeNA = false,
                      multi = false,
                      min = 0,
                      max = 10,
                    } = {
                      ...currQuestion,
                    };
                    const lng = language.slice(0, 2);
                    const submitButtonMessage = groupIndex === groups.length - 1 && index === questions.length - 1;
                    const isRatio = type === 'multiple-selection' && !multi
                    const isPointedSelection = type === 'pointed-multiple-selection'
                    const isDate = type === 'date';
                    const isDisabled =
                      ((!formData.values[questionId] || isEmpty(formData.values[questionId])) && required) ||
                      formData.isSubmitting;
                    const questionLabel = currQuestion[lng] || currQuestion[`${lng}-title`] || label || title;
                    const questionContent = currQuestion[`${lng}-content`] || content;
                    const questionOptions = currQuestion[`${lng}-options`] || currQuestion.options || [];

                    if (index + 1 !== question) return null;
                    return (
                      <Fade key={questionId} triggerOnce>
                        <div className="form-input">
                          <FormControlSurveys
                            formikProps={formData}
                            control={type}
                            name={questionId}
                            label={questionLabel}
                            content={questionContent}
                            gif={gif}
                            options={questionOptions}
                            isMulti={multi}
                            isRequired={required}
                            includeNA={includeNA}
                            min={Number(min)}
                            max={Number(max)}
                            handleNext={() =>
                              questionhandler({
                                goNextGroup: index + 1 === questions.length,
                                submitForm: formData.submitForm,
                              })
                            }
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>
                        {!isRatio && !isPointedSelection && (
                          <Button
                              className={`evie-survey-next ${isDisabled ? 'evie-next-disabled' : ''} ${isDate ? 'evie-survey--date' : ''}`}
                              onClick={() =>
                                questionhandler({
                                  goNextGroup: index + 1 === questions.length,
                                  submitForm: formData.submitForm,
                                })
                              }
                              type="button"
                              disabled={isDisabled}
                            >
                              {submitButtonMessage ? t('button.plan') : t('button.next')}
                            </Button>
                        )}
                      </Fade>
                    );
                  })}
                </Form>
              )}
            </Formik>
          </div>
        );
      })}
    </section>
  );
};

Survey.propTypes = {
  survey: PropTypes.shape({
    title: PropTypes.string.isRequired,
    content: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  }).isRequired,
  completeSurvey: PropTypes.func.isRequired,
};

export default Survey;
